import { Badge } from "@volca/ui/components/ui/badge";
import { cn } from "@volca/ui/lib/utils";
import { useReportCurrency } from "@/profitability/currency";
import { StatusDot } from "@/profitability/review/finding-status";
import { money, rate, STATUS_LABEL } from "@/profitability/review/labels";
import type { FindingStatus } from "@/profitability/review/types";
import type { ReportRow } from "@/profitability/types";
import type { RecordObject } from "./objects";
import { RecordChip } from "./record-chip";

/**
 * One way to show a job number, a named record, a figure, or a status.
 * The review row, a finding's table, and an activity change all use it.
 */
export type RecordValueSpec =
  | { kind: "badge"; label: string }
  | {
      kind: "record";
      object: Pick<RecordObject, "icon" | "name"> & Partial<RecordObject>;
      name: string;
      trade?: string | null;
      row?: ReportRow;
      onOpen?: () => void;
    }
  | { kind: "money"; amount: number; short?: boolean; loss?: boolean }
  | { kind: "rate"; value: number; loss?: boolean; label?: string }
  | { kind: "status"; status: FindingStatus }
  | { kind: "text"; text: string };

export const RecordValue = ({
  spec,
  className,
}: {
  spec: RecordValueSpec;
  className?: string;
}) => {
  const currency = useReportCurrency();
  switch (spec.kind) {
    case "badge":
      return (
        <Badge
          variant="secondary"
          size="sm"
          className={cn("h-5 max-w-full tabular-nums", className)}
        >
          <span className="truncate">{spec.label}</span>
        </Badge>
      );
    case "record":
      return (
        <RecordChip
          object={spec.object}
          name={spec.name}
          trade={spec.trade}
          row={spec.row}
          onOpen={spec.onOpen}
        />
      );
    case "money":
      return (
        <span
          className={cn(
            "text-sm text-ink tabular-nums",
            { "text-danger": spec.loss },
            className,
          )}
        >
          {spec.short
            ? `−${money(spec.amount, currency)}`
            : money(spec.amount, currency)}
        </span>
      );
    case "rate":
      return (
        <span
          className={cn(
            "text-sm text-ink tabular-nums",
            { "text-danger": spec.loss },
            className,
          )}
        >
          {spec.label ? `${spec.label} ${rate(spec.value)}` : rate(spec.value)}
        </span>
      );
    case "status":
      return (
        <span
          className={cn(
            "inline-flex items-center gap-1.5 text-sm text-ink",
            className,
          )}
        >
          <StatusDot status={spec.status} />
          {STATUS_LABEL[spec.status]}
        </span>
      );
    case "text":
      return (
        <span className={cn("text-sm text-ink", className)}>{spec.text}</span>
      );
    default: {
      const unreachable: never = spec;
      return unreachable;
    }
  }
};
